import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { Plus, Search, Users, Mail, Building2, BadgeCheck, Fingerprint } from 'lucide-react';
import Spinner from './Spinner.js';

const AdminEmployees = ({ db }) => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  useEffect(() => {
    const employeesQuery = query(collection(db, 'employees'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(employeesQuery, (snapshot) => {
      const nextEmployees = snapshot.docs.map((employeeDoc) => ({
        id: employeeDoc.id,
        ...employeeDoc.data()
      }));
      setEmployees(nextEmployees);
      setLoading(false);
    }, (error) => {
      console.error('Error loading employees:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const filteredEmployees = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return employees.filter((employee) => {
      const normalizedStatus = String(employee.status || '').toLowerCase();
      const matchesSearch =
        !normalizedSearch ||
        employee.name?.toLowerCase().includes(normalizedSearch) ||
        employee.email?.toLowerCase().includes(normalizedSearch) ||
        employee.employeeId?.toLowerCase().includes(normalizedSearch) ||
        employee.department?.toLowerCase().includes(normalizedSearch) ||
        employee.designation?.toLowerCase().includes(normalizedSearch) ||
        employee.branch?.toLowerCase().includes(normalizedSearch);

      const matchesStatus = statusFilter === 'All' || normalizedStatus === statusFilter.toLowerCase();
      return matchesSearch && matchesStatus;
    });
  }, [employees, searchTerm, statusFilter]);

  const activeCount = employees.filter((employee) => String(employee.status || '').toLowerCase() === 'active').length;
  const inactiveCount = employees.filter((employee) => String(employee.status || '').toLowerCase() === 'inactive').length;

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px' }}>
      <div style={headerStyle}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, color: '#0f172a' }}>
            Employee Directory
          </h1>
          <p style={{ fontSize: '14px', color: '#64748b', margin: '6px 0 0 0' }}>
            Central list of employee records for admin management.
          </p>
        </div>
        <button onClick={() => navigate('/admin/employees/new')} style={primaryButtonStyle}>
          <Plus size={16} />
          Add Employee
        </button>
      </div>

      <div style={summaryGridStyle}>
        <SummaryCard icon={Users} label="Total Employees" value={employees.length} accent="#2563eb" />
        <SummaryCard icon={BadgeCheck} label="Active" value={activeCount} accent="#16a34a" />
        <SummaryCard icon={Fingerprint} label="Inactive" value={inactiveCount} accent="#dc2626" />
      </div>

      <div style={filterBarStyle}>
        <div style={{ position: 'relative', flex: 1, minWidth: '260px' }}>
          <Search size={18} style={searchIconStyle} />
          <input
            type="text"
            placeholder="Search by name, email, employee ID, department..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            style={searchInputStyle}
          />
        </div>

        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} style={selectStyle}>
          <option value="All">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div style={tableContainerStyle}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={theadStyle}>
              <th style={thStyle}>Employee</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Org</th>
              <th style={thStyle}>Auth</th>
              <th style={thStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  No employees match the current filters.
                </td>
              </tr>
            ) : (
              filteredEmployees.map((employee) => (
                <tr key={employee.id} style={trStyle}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: '700', color: '#0f172a' }}>{employee.name || 'Unnamed Employee'}</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '3px' }}>
                      {employee.employeeId || '--'}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#334155' }}>
                      <Mail size={14} />
                      <span>{employee.email || '--'}</span>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#334155' }}>
                        <Building2 size={14} />
                        <span>{[employee.department, employee.designation].filter(Boolean).join(' - ') || '--'}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {[employee.branch, employee.region].filter(Boolean).join(' | ') || '--'}
                      </div>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={pillStyle}>{employee.firebaseUid || '--'}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={statusBadgeStyle(employee.status)}>
                      {employee.status || 'unknown'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, accent }) => (
  <div style={summaryCardStyle}>
    <div style={{ ...summaryIconStyle, borderColor: `${accent}33`, color: accent }}>
      <Icon size={18} />
    </div>
    <div>
      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a' }}>{value}</div>
    </div>
  </div>
);

const statusBadgeStyle = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'active') {
    return {
      ...badgeStyle,
      backgroundColor: '#ecfdf5',
      color: '#15803d',
      borderColor: '#bbf7d0'
    };
  }
  if (normalized === 'inactive') {
    return {
      ...badgeStyle,
      backgroundColor: '#fef2f2',
      color: '#b91c1c',
      borderColor: '#fecaca'
    };
  }
  return {
    ...badgeStyle,
    backgroundColor: '#f8fafc',
    color: '#475569',
    borderColor: '#e2e8f0'
  };
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '16px',
  marginBottom: '20px'
};

const summaryGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '14px',
  marginBottom: '18px'
};

const summaryCardStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '16px 18px',
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
};

const primaryButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '11px 16px',
  borderRadius: '10px',
  border: 'none',
  backgroundColor: '#2563eb',
  color: 'white',
  fontSize: '14px',
  fontWeight: '700',
  cursor: 'pointer',
  boxShadow: '0 1px 2px rgba(37, 99, 235, 0.18)'
};

const summaryIconStyle = {
  width: '38px',
  height: '38px',
  borderRadius: '10px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid'
};

const filterBarStyle = {
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  flexWrap: 'wrap',
  marginBottom: '16px'
};

const tableContainerStyle = {
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
};

const theadStyle = {
  backgroundColor: '#f8fafc'
};

const thStyle = {
  textAlign: 'left',
  padding: '14px 16px',
  fontSize: '12px',
  fontWeight: '700',
  color: '#475569',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  borderBottom: '1px solid #e2e8f0'
};

const tdStyle = {
  padding: '16px',
  borderBottom: '1px solid #eef2f7',
  verticalAlign: 'top',
  fontSize: '14px',
  color: '#0f172a'
};

const trStyle = {
  transition: 'background-color 0.15s ease'
};

const searchIconStyle = {
  position: 'absolute',
  left: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#94a3b8'
};

const searchInputStyle = {
  width: '100%',
  padding: '11px 14px 11px 40px',
  borderRadius: '10px',
  border: '1px solid #dbe3ee',
  backgroundColor: 'white',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box'
};

const selectStyle = {
  minWidth: '170px',
  padding: '11px 14px',
  borderRadius: '10px',
  border: '1px solid #dbe3ee',
  backgroundColor: 'white',
  fontSize: '14px',
  outline: 'none'
};

const pillStyle = {
  display: 'inline-flex',
  maxWidth: '280px',
  padding: '6px 10px',
  borderRadius: '999px',
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  color: '#475569',
  fontSize: '12px',
  fontWeight: '600',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const badgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6px 10px',
  borderRadius: '999px',
  border: '1px solid',
  fontSize: '12px',
  fontWeight: '700',
  textTransform: 'capitalize'
};

export default AdminEmployees;
